import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtRp  = n => 'Rp ' + (n || 0).toLocaleString('id-ID');
const fmtDT  = d => d ? new Date(d).toLocaleString('id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-';
const today  = () => new Date().toISOString().split('T')[0];

const EXPENSE_CATS = ['Bahan Baku', 'Operasional', 'Gaji/Bonus', 'Utilitas', 'Transportasi', 'Lain-lain'];
const INCOME_CATS  = ['Titipan', 'Penjualan Lain', 'Lain-lain'];

const S = {
  card:  { background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)', boxShadow: 'var(--shadow)' },
  label: { fontSize: '.68rem', fontFamily: "'DM Mono',monospace", letterSpacing: '1.2px', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 4, display: 'block' },
  inp:   { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: '.88rem', background: '#FAFAFA', outline: 'none' },
  btn:   (bg, c = 'white') => ({ padding: '9px 18px', borderRadius: 8, background: bg, color: c, border: 'none', fontSize: '.82rem', fontWeight: 600, cursor: 'pointer' }),
};

// ─── Form Tambah Transaksi ─────────────────────────────────────────────────────
function TambahModal({ users, activeShift, onClose, onSaved }) {
  const [type, setType]         = useState('expense');
  const [category, setCategory] = useState('Bahan Baku');
  const [amount, setAmount]     = useState('');
  const [note, setNote]         = useState('');
  const [staffId, setStaffId]   = useState('');
  const [saving, setSaving]     = useState(false);

  const cats = type === 'expense' ? EXPENSE_CATS : INCOME_CATS;

  // Reset kategori saat type berubah
  const handleTypeChange = t => { setType(t); setCategory(t === 'expense' ? EXPENSE_CATS[0] : INCOME_CATS[0]); };

  async function simpan() {
    if (!amount || parseInt(amount) <= 0) return alert('Isi nominal terlebih dahulu');
    setSaving(true);
    const staff = users.find(u => u.id === staffId);
    const { error } = await supabase.from('transactions').insert({
      type,
      category,
      amount: parseInt(amount),
      note,
      staff_id:   staffId || null,
      staff_name: staff?.name || '',
      shift_id:   activeShift?.id || null,
    });
    if (error) { alert('Gagal menyimpan: ' + error.message); setSaving(false); return; }
    onSaved();
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 999 }}>
      <div style={{ ...S.card, width: '100%', maxWidth: 480, borderBottomLeftRadius: 0, borderBottomRightRadius: 0, padding: '22px 20px 28px', animation: 'fadeUp .2s ease' }}>
        <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.25rem', marginBottom: 16 }}>Tambah Transaksi Kas</div>

        {/* Toggle Tipe */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)' }}>
          {[['expense', '💸 Pengeluaran'], ['income', '💰 Pemasukan']].map(([t, l]) => (
            <button key={t} onClick={() => handleTypeChange(t)} style={{ flex: 1, padding: '9px', border: 'none', background: type === t ? (t === 'expense' ? '#FFEBEE' : '#E8F5E9') : 'white', color: type === t ? (t === 'expense' ? '#C62828' : '#1B5E20') : 'var(--muted)', fontWeight: type === t ? 700 : 400, fontSize: '.82rem', cursor: 'pointer', transition: 'all .15s' }}>
              {l}
            </button>
          ))}
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={S.label}>Kategori</label>
          <select value={category} onChange={e => setCategory(e.target.value)} style={S.inp}>
            {cats.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={S.label}>Nominal (Rp)</label>
          <input type='number' placeholder='0' value={amount} onChange={e => setAmount(e.target.value)} style={S.inp} />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={S.label}>Keterangan</label>
          <input type='text' placeholder='Contoh: beli tepung 5kg...' value={note} onChange={e => setNote(e.target.value)} style={S.inp} />
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={S.label}>Dicatat oleh</label>
          <select value={staffId} onChange={e => setStaffId(e.target.value)} style={S.inp}>
            <option value=''>-- Pilih staff --</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>

        {activeShift && (
          <div style={{ fontSize: '.73rem', color: '#1B5E20', background: '#F0FBF4', borderRadius: 8, padding: '7px 12px', marginBottom: 14 }}>
            Akan dicatat ke shift: <strong>{activeShift.opened_by_name}</strong> · {fmtDT(activeShift.opened_at)}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ ...S.btn('white', 'var(--muted)'), border: '1px solid var(--border)', flex: 1 }}>Batal</button>
          <button onClick={simpan} disabled={saving} style={{ ...S.btn(type === 'expense' ? '#E53935' : '#27AE60'), flex: 2 }}>
            {saving ? 'Menyimpan...' : (type === 'expense' ? '💸 Simpan Pengeluaran' : '💰 Simpan Pemasukan')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function KasHarian() {
  const [txs, setTxs]           = useState([]);
  const [users, setUsers]       = useState([]);
  const [activeShift, setActiveShift] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [showModal, setModal]   = useState(false);
  const [typeFilter, setType]   = useState('semua');
  const [dateFilter, setDate]   = useState(today());
  const [cancelling, setCancelling] = useState(null);

  useEffect(() => { load(); }, [dateFilter]);

  async function load() {
    const start = dateFilter + 'T00:00:00';
    const end   = dateFilter + 'T23:59:59';

    const [txRes, uRes, shiftRes] = await Promise.all([
      supabase.from('transactions')
        .select('*')
        .gte('created_at', start)
        .lte('created_at', end)
        .order('created_at', { ascending: false }),
      supabase.from('users').select('id,name,role').eq('is_active', true),
      supabase.from('shifts').select('id,opened_by_name,opened_at').eq('status', 'open').maybeSingle(),
    ]);

    setTxs(txRes.data || []);
    setUsers(uRes.data || []);
    setActiveShift(shiftRes.data || null);
    setLoading(false);
  }

  async function batalTx(id) {
    if (!confirm('Batalkan transaksi ini?')) return;
    setCancelling(id);
    await supabase.from('transactions').update({
      is_cancelled: true,
      cancelled_by: 'admin',
      cancelled_at: new Date().toISOString(),
    }).eq('id', id);
    setCancelling(null);
    load();
  }

  const filtered = txs.filter(t => typeFilter === 'semua' || t.type === typeFilter);
  const aktif    = filtered.filter(t => !t.is_cancelled);
  const totalExp = aktif.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
  const totalInc = aktif.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
  const net      = totalInc - totalExp;

  return (
    <div style={{ padding: 'clamp(16px,4vw,28px) clamp(12px,4vw,24px) 60px' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.5rem' }}>Kas Harian</div>
          <div style={{ fontSize: '.82rem', color: 'var(--muted)' }}>Pengeluaran & pemasukan non-order</div>
        </div>
        <button onClick={() => setModal(true)} style={{ ...S.btn('#CD5226', '#fff') }}>+ Tambah</button>
      </div>

      {/* Filter Tanggal */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input type='date' value={dateFilter} onChange={e => setDate(e.target.value)} style={{ ...S.inp, width: 'auto', padding: '7px 12px' }} />
        <div style={{ display: 'flex', gap: 6 }}>
          {[['semua', 'Semua'], ['expense', '💸 Pengeluaran'], ['income', '💰 Pemasukan']].map(([v, l]) => (
            <button key={v} onClick={() => setType(v)} style={{ padding: '6px 14px', borderRadius: 20, border: '1px solid var(--border)', background: typeFilter === v ? '#CD5226' : 'white', color: typeFilter === v ? '#fff' : 'var(--muted)', fontSize: '.75rem', cursor: 'pointer' }}>{l}</button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, marginBottom: 18 }}>
        {[
          { label: 'Pengeluaran', val: fmtRp(totalExp), bg: '#FFEBEE', c: '#C62828' },
          { label: 'Pemasukan', val: fmtRp(totalInc), bg: '#E8F5E9', c: '#1B5E20' },
          { label: 'Selisih', val: fmtRp(Math.abs(net)), bg: net >= 0 ? '#EFF6FF' : '#FFF3E0', c: net >= 0 ? '#1C5BD4' : '#E65100', prefix: net >= 0 ? '▲' : '▼' },
        ].map(({ label, val, bg, c, prefix }) => (
          <div key={label} style={{ ...S.card, padding: '13px 15px', background: bg, border: 'none' }}>
            <div style={{ fontSize: '.65rem', fontFamily: "'DM Mono',monospace", letterSpacing: '.8px', textTransform: 'uppercase', color: c, opacity: .7, marginBottom: 4 }}>{label}</div>
            <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.1rem', color: c }}>{prefix && <span style={{ fontSize: '.8rem', marginRight: 2 }}>{prefix}</span>}{val}</div>
          </div>
        ))}
      </div>

      {/* Info shift aktif */}
      {activeShift && (
        <div style={{ fontSize: '.75rem', color: '#166534', background: '#F0FBF4', borderRadius: 8, padding: '7px 14px', marginBottom: 12 }}>
          🟢 Shift aktif: <strong>{activeShift.opened_by_name}</strong> sejak {fmtDT(activeShift.opened_at)}
        </div>
      )}

      {/* Daftar Transaksi */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>Memuat...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)', fontSize: '.84rem' }}>
          Belum ada transaksi hari ini.
        </div>
      ) : filtered.map(tx => {
        const isExp    = tx.type === 'expense';
        const isBatal  = tx.is_cancelled;
        return (
          <div key={tx.id} style={{ ...S.card, marginBottom: 8, opacity: isBatal ? .5 : 1, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'center' }}>
              {/* Icon */}
              <div style={{ width: 38, height: 38, borderRadius: 10, background: isBatal ? '#F5F5F5' : (isExp ? '#FFEBEE' : '#E8F5E9'), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                {isBatal ? '🚫' : isExp ? '💸' : '💰'}
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 2, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: '.88rem' }}>{tx.category}</span>
                  {isBatal && <span style={{ fontFamily: "'DM Mono',monospace", fontSize: '.6rem', background: '#FFEBEE', color: '#C62828', borderRadius: 20, padding: '1px 8px' }}>BATAL</span>}
                </div>
                <div style={{ fontSize: '.75rem', color: 'var(--muted)' }}>
                  {tx.note || '(tanpa keterangan)'}
                  {tx.staff_name && <> · <span style={{ color: 'var(--dark)' }}>{tx.staff_name}</span></>}
                </div>
                <div style={{ fontSize: '.68rem', color: 'var(--muted)', marginTop: 1 }}>{fmtDT(tx.created_at)}</div>
              </div>

              {/* Nominal + Aksi */}
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1rem', color: isBatal ? 'var(--muted)' : (isExp ? '#C62828' : '#1B5E20') }}>
                  {isExp ? '−' : '+'}{fmtRp(tx.amount)}
                </div>
                {!isBatal && (
                  <button onClick={() => batalTx(tx.id)} disabled={cancelling === tx.id} style={{ marginTop: 4, padding: '2px 10px', borderRadius: 6, background: '#FFEBEE', color: '#C62828', border: 'none', fontSize: '.65rem', cursor: 'pointer', fontFamily: "'DM Mono',monospace" }}>
                    {cancelling === tx.id ? '...' : 'Batal'}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Modal */}
      {showModal && (
        <TambahModal
          users={users}
          activeShift={activeShift}
          onClose={() => setModal(false)}
          onSaved={() => { setModal(false); load(); }}
        />
      )}
    </div>
  );
}
